import * as React from 'react';
import './TagsInput.scss';
import { t } from '@deltasierra/shared';
import { Button } from '../../Button';

export type TagsInputProps = {
    dataCy?: string;
    tags: ReadonlyArray<{ id: string; tags: ReadonlyArray<string> }>;
    onChange: (newTags: Array<{ id: string; tags: string[] }>) => void;
    label: string;
};

export const TagsInput: React.FunctionComponent<TagsInputProps> = ({ dataCy, label, onChange, tags }) => {
    const [text, setText] = React.useState('');
    const tagMap: Record<string, number> = React.useMemo(() => {
        const tempMap: Record<string, number> = {};
        tags.forEach(tagItem =>
            tagItem.tags.forEach((tag: string) => {
                if (!tempMap[tag]) {
                    tempMap[tag] = 1;
                } else {
                    tempMap[tag]++;
                }
            }),
        );
        return tempMap;
    }, [tags]);

    const handleAddTag = (tagToAdd: string) => {
        if (tagToAdd && tagToAdd.trim()) {
            onChange(
                tags.map(tag => ({
                    ...tag,
                    tags: !tag.tags.includes(tagToAdd) ? [...tag.tags, tagToAdd] : tag.tags.slice(),
                })),
            );
            setText('');
        }
    };

    const handleRemoveTag = (tagToRemove: string) => {
        if (tagToRemove && tagToRemove.trim()) {
            onChange(tags.map(tag => ({ ...tag, tags: tag.tags.filter(val => val !== tagToRemove) })));
            setText('');
        }
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const SPACE_KEY = ' ';
        const ENTER_KEY = 'Enter';
        if (event.key === SPACE_KEY || event.key === ENTER_KEY) {
            event.stopPropagation();
            event.preventDefault();
            handleAddTag(text);
        }
    };

    const allAssetsTags = Object.keys(tagMap)
        .filter(tag => tagMap[tag] >= tags.length)
        .map((tag, index) => {
            let tagLabel = tag;
            if (tagMap[tag] > 1) {
                tagLabel += ` (${tagMap[tag]})`;
            }
            return <TagLabel key={index} label={tagLabel} onClick={() => handleRemoveTag(tag)} />;
        });

    const someAssetsTags = Object.keys(tagMap)
        .filter(tag => tagMap[tag] < tags.length && tagMap[tag] > 0)
        .map((tag, index) => (
            <TagLabel
                key={index}
                label={`${tag} (${tagMap[tag]})`}
                style={{ backgroundColor: '#999' }}
                onClick={() => handleRemoveTag(tag)}
            />
        ));

    return (
        <div className="edit-tag-body">
            {allAssetsTags.length > 0 && (
                <div className="row tags-input">
                    {tags.length > 1 ? (
                        <label className="control-label col-md-3">{t('COMMON.ALL_ASSETS')}</label>
                    ) : (
                        <label className="control-label col-md-2">{label}</label>
                    )}
                    <div className="col-md-8">
                        <div className="tags">{allAssetsTags}</div>
                    </div>
                </div>
            )}
            {tags.length > 1 && someAssetsTags.length > 0 && (
                <div className="row tags-input">
                    <label className="control-label col-md-3">{t('COMMON.SOME_ASSETS')}</label>
                    <div className="col-md-8">
                        <div className="tags">{tags.length > 1 && someAssetsTags}</div>
                    </div>
                </div>
            )}
            <div className="input-group">
                <input
                    className="form-control"
                    data-cy={dataCy}
                    type="text"
                    value={text.trim()}
                    onChange={event => setText(event.target.value)}
                    onKeyPress={event => handleKeyPress(event)}
                />
                <span className="input-group-btn">
                    <Button theme="success" onClick={() => handleAddTag(text)}>
                        {t('COMMON.ADD_TAG')}
                    </Button>
                </span>
            </div>
        </div>
    );
};
TagsInput.displayName = 'TagsInput';

type TagLabelProps = {
    label: string;
    onClick: () => void;
    style?: React.CSSProperties;
};

const TagLabel: React.FunctionComponent<TagLabelProps> = ({ label, onClick, style }) => (
    <span className="label label-default" style={style} onClick={onClick}>
        {/* eslint-disable-next-line react/jsx-no-literals */}
        {label} <span aria-hidden="true">&times;</span>
    </span>
);
